// src/pages/admin/AdminShowcase.tsx
import { useState } from "react";
import {
  useAdminGetShowcasesQuery,
  useAdminCreateShowcaseMutation,
  useAdminUpdateShowcaseMutation,
  useAdminDeleteShowcaseMutation,
  useAdminPublishShowcaseMutation,
  useAdminUnpublishShowcaseMutation,
  useAdminCancelShowcaseMutation,
  useAdminUpdateShowcaseParticipantsMutation,
} from "../../app/api/adminExtApi";
import { useAdminGetStudentsQuery } from "../../app/api/adminApi";
import type { AdminStudent } from "../../app/api/adminApi";
import type {
  AdminShowcase,
  CreateShowcaseBody,
  ShowcaseFormat,
  ShowcaseTrack,
} from "../../app/api/adminExtApi";
import type { StudentTrack } from "../../app/api/authApi";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  Button,
  Select,
  Avatar,
  TrackChip,
  formatDate,
  trackConfig,
} from "./AdminShared";

import { CalendarDays, Check, MapPin, Mic, Timer, Users } from "lucide-react";
function currentAcademicYear() {
  const y = new Date().getFullYear();
  return new Date().getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

const FORMAT_LABELS: Record<ShowcaseFormat, string> = {
  in_person: "In-person",
  online: "Online",
  hybrid: "Hybrid",
  other: "Other",
};

/* ─── Showcase form (create / edit) ─────────────────────────────── */
function ShowcaseForm({
  existing,
  onDone,
}: {
  existing?: AdminShowcase;
  onDone: () => void;
}) {
  const [create, { isLoading: creating }] = useAdminCreateShowcaseMutation();
  const [update, { isLoading: updating }] = useAdminUpdateShowcaseMutation();

  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [track, setTrack] = useState(existing?.track ?? "foundation_builders");
  const [quarter, setQuarter] = useState(existing?.quarter ?? "Q1");
  const [academicYear, setAcademicYear] = useState(
    existing?.academicYear ?? currentAcademicYear(),
  );
  const [format, setFormat] = useState<ShowcaseFormat>(
    existing?.format ?? "in_person",
  );
  const [scheduledAt, setScheduledAt] = useState(
    existing?.scheduledAt ? existing.scheduledAt.split("T")[0] : "",
  );
  const [location, setLocation] = useState(existing?.location ?? "");
  const [duration, setDuration] = useState(
    String(existing?.durationMinutes ?? 60),
  );
  const [extJudges, setExtJudges] = useState(existing?.externalJudges ?? false);
  const [parentsInvited, setParentsInvited] = useState(
    existing?.parentsInvited ?? true,
  );
  const [error, setError] = useState("");

  const isLoading = creating || updating;

  const handleSubmit = async () => {
    if (!name || !scheduledAt) {
      setError("Name and date are required");
      return;
    }
    setError("");
    const body: CreateShowcaseBody = {
      name,
      description: description || undefined,
      track,
      quarter,
      academicYear,
      format,
      scheduledAt,
      location: location || undefined,
      durationMinutes: Number(duration),
      externalJudges: extJudges,
      parentsInvited,
    };
    try {
      if (existing) await update({ id: existing.id, body }).unwrap();
      else await create(body).unwrap();
      onDone();
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed to save");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]";
  const Toggle = ({
    on,
    set,
    label,
  }: {
    on: boolean;
    set: (v: boolean) => void;
    label: string;
  }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div
        onClick={() => set(!on)}
        className="h-5 w-9 rounded-full transition-colors"
        style={{ background: on ? "#1b2d5e" : "#dde2ec" }}
      >
        <div
          className={`mt-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`}
        />
      </div>
      <span className="text-xs font-semibold text-[#3b4560]">{label}</span>
    </label>
  );

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-4">
      <h3 className="text-base font-bold text-[#16213e]">
        {existing ? "Edit showcase" : "Create showcase"}
      </h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Showcase name *
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q1 Innovation Showcase"
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Brief description for parents…"
            className={inputCls + " mt-1.5 resize-none"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Track
          </label>
          <select
            value={track}
            onChange={(e) => setTrack(e.target.value as ShowcaseTrack)}
            className={inputCls + " mt-1.5"}
          >
            <option value="foundation_builders">
              Foundation Builders (6–9)
            </option>
            <option value="explorers">Explorers (9–12)</option>
            <option value="innovators">Innovators (13–18)</option>
            <option value="all">All tracks</option>
          </select>
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Quarter
          </label>
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value)}
            className={inputCls + " mt-1.5"}
          >
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
          </select>
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Academic year
          </label>
          <input
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            placeholder="2025-2026"
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Format
          </label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ShowcaseFormat)}
            className={inputCls + " mt-1.5"}
          >
            {(Object.entries(FORMAT_LABELS) as [ShowcaseFormat, string][]).map(
              ([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ),
            )}
          </select>
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Date *
          </label>
          <input
            type="date"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Duration (minutes)
          </label>
          <input
            type="number"
            min="15"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Location / link
          </label>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. FSL Studio, Abuja or Zoom link"
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-6">
          <Toggle
            on={parentsInvited}
            set={setParentsInvited}
            label="Parents invited"
          />
          <Toggle on={extJudges} set={setExtJudges} label="External judges" />
        </div>
      </div>
      {error && <p className="text-xs text-[#e57373]">{error}</p>}
      <div className="flex gap-3">
        <Button variant="secondary" className="flex-1" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading
            ? "Saving…"
            : existing
              ? "Save changes"
              : "Create showcase"}
        </Button>
      </div>
    </div>
  );
}

/* ─── Participants panel ─────────────────────────────────────────── */
function ParticipantsPanel({
  showcase,
  onClose,
}: {
  showcase: AdminShowcase;
  onClose: () => void;
}) {
  const { data: studentsData } = useAdminGetStudentsQuery({
    track:
      showcase.track !== "all" ? (showcase.track as StudentTrack) : undefined,
    limit: 200,
  });
  const [updateParticipants, { isLoading }] =
    useAdminUpdateShowcaseParticipantsMutation();
  const students = studentsData?.data ?? [];

  const [selected, setSelected] = useState<Set<string>>(
    new Set(showcase.participantIds),
  );
  const [error, setError] = useState("");

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleSave = async () => {
    try {
      await updateParticipants({
        id: showcase.id,
        childIds: Array.from(selected),
      }).unwrap();
      onClose();
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed");
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[420px]">
        <div className="flex items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-base font-bold text-[#16213e]">Participants</h2>
            <p className="text-xs text-[#8189a0]">
              {showcase.name} · {selected.size} selected
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {students.length === 0 ? (
            <p className="text-sm text-[#8189a0] text-center py-8">
              No enrolled students found for this track.
            </p>
          ) : (
            students.map((s: AdminStudent) => {
              const isOn = selected.has(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  className={[
                    "flex w-full items-center gap-3 rounded-[14px] border-2 px-3 py-2.5 text-left transition-all",
                    isOn
                      ? "border-[#1b2d5e] bg-[#ebeff7]"
                      : "border-[#dde2ec] bg-white hover:border-[#c9cfe0]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[6px] border-2",
                      isOn
                        ? "border-[#1b2d5e] bg-[#1b2d5e]"
                        : "border-[#c9cfe0]",
                    ].join(" ")}
                  >
                    {isOn && (
                      <svg
                        className="h-3 w-3 text-white"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6l3 3 5-5"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <Avatar
                    firstName={s.firstName}
                    lastName={s.lastName}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-xs font-bold truncate ${isOn ? "text-[#4a44b0]" : "text-[#16213e]"}`}
                    >
                      {s.firstName} {s.lastName}
                    </p>
                    <p className="text-[11.5px] text-[#8189a0]">Age {s.age}</p>
                  </div>
                  <TrackChip track={s.track} />
                </button>
              );
            })
          )}
        </div>
        <div className="border-t border-[#ebeff7] px-6 py-4 space-y-2">
          {error && <p className="text-xs text-[#e57373]">{error}</p>}
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Saving…" : `Save participants (${selected.size})`}
          </Button>
        </div>
      </div>
    </>
  );
}

/* ─── Showcase card ─────────────────────────────────────────────── */
function ShowcaseCard({
  showcase,
  onEdit,
  onParticipants,
}: {
  showcase: AdminShowcase;
  onEdit: () => void;
  onParticipants: () => void;
}) {
  const [publish, { isLoading: publishing }] =
    useAdminPublishShowcaseMutation();
  const [unpublish, { isLoading: unpublishing }] =
    useAdminUnpublishShowcaseMutation();
  const [cancel, { isLoading: cancelling }] = useAdminCancelShowcaseMutation();
  const [remove, { isLoading: removing }] = useAdminDeleteShowcaseMutation();
  const [err, setErr] = useState("");

  const tc = trackConfig(
    showcase.track === "all" ? "foundation_builders" : showcase.track,
  );
  const daysLeft = showcase.daysLeft;

  const statusColor = showcase.isCancelled
    ? { bg: "#fff3f3", text: "#9b2a2a", label: "Cancelled" }
    : showcase.isPublished
      ? { bg: "#edfaf4", text: "#2a7a56", label: "Published" }
      : { bg: "#f1f3f8", text: "#8189a0", label: "Draft" };

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-5 shadow-[0_2px_8px_rgba(27,45,94,0.04)] space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
              style={{
                background: tc.bg,
                color: tc.text,
                border: `1px solid ${tc.border}`,
              }}
            >
              {showcase.track === "all" ? "All tracks" : tc.label}
            </span>
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-bold"
              style={{ background: statusColor.bg, color: statusColor.text }}
            >
              {statusColor.label}
            </span>
          </div>
          <h3 className="text-base font-bold text-[#16213e] truncate">
            {showcase.name}
          </h3>
          {showcase.description && (
            <p className="text-xs text-[#5a6278] mt-0.5 line-clamp-2">
              {showcase.description}
            </p>
          )}
        </div>
        {daysLeft != null && daysLeft > 0 && !showcase.isCancelled && (
          <div className="flex-shrink-0 rounded-[12px] bg-[#ebeff7] px-3 py-1.5 text-center">
            <p className="text-lg font-bold text-[#1b2d5e] leading-none">
              {daysLeft}
            </p>
            <p className="text-[11px] font-bold uppercase text-[#1b2d5e]">
              days
            </p>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-xs text-[#5a6278]">
        <p><CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{showcase.dateLabel ?? formatDate(showcase.scheduledAt)}</p>
        <p>
          <Timer className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{showcase.durationMinutes} min · {FORMAT_LABELS[showcase.format]}
        </p>
        {showcase.location && (
          <p className="col-span-2"><MapPin className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{showcase.location}</p>
        )}
        <p>
          <Users className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{showcase.participantCount} participant
          {showcase.participantCount !== 1 ? "s" : ""}
        </p>
        <p>
          {showcase.parentsInvited ? <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Parents invited</> : "—"}
          {showcase.externalJudges ? " · Judges" : ""}
        </p>
      </div>

      {/* Participants preview */}
      {showcase.participants.length > 0 && (
        <div className="flex -space-x-1.5">
          {showcase.participants
            .slice(0, 8)
            .map((p: AdminShowcase["participants"][number]) => (
              <Avatar
                key={p.id}
                firstName={p.firstName}
                lastName={p.lastName}
                size="sm"
                className="border-2 border-white"
              />
            ))}
          {showcase.participantCount > 8 && (
            <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-[#ebeff7] text-[11px] font-bold text-[#1b2d5e]">
              +{showcase.participantCount - 8}
            </span>
          )}
        </div>
      )}

      {err && <p className="text-xs text-[#e57373]">{err}</p>}

      {/* Actions */}
      {!showcase.isCancelled && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-[#f7f8fb]">
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button variant="secondary" size="sm" onClick={onParticipants}>
            <Users className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Participants
          </Button>
          {!showcase.isPublished ? (
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                try {
                  await publish(showcase.id).unwrap();
                } catch (e: any) {
                  setErr(e?.data?.error ?? "Failed");
                }
              }}
              disabled={publishing}
            >
              {publishing ? "…" : "Publish"}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await unpublish(showcase.id).unwrap();
                } catch (e: any) {
                  setErr(e?.data?.error ?? "Failed");
                }
              }}
              disabled={unpublishing}
            >
              {unpublishing ? "…" : "Unpublish"}
            </Button>
          )}
          <Button
            variant="danger"
            size="sm"
            onClick={async () => {
              if (!window.confirm("Cancel this showcase?")) return;
              try {
                await cancel(showcase.id).unwrap();
              } catch (e: any) {
                setErr(e?.data?.error ?? "Failed");
              }
            }}
            disabled={cancelling}
          >
            {cancelling ? "…" : "Cancel"}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={async () => {
              if (!window.confirm("Delete this showcase permanently?")) return;
              try {
                await remove(showcase.id).unwrap();
              } catch (e: any) {
                setErr(e?.data?.error ?? "Failed");
              }
            }}
            disabled={removing}
          >
            {removing ? "…" : "Delete"}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export default function AdminShowcase() {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AdminShowcase | null>(null);
  const [participants, setParticipants] = useState<AdminShowcase | null>(null);
  const [filterTrack, setFilterTrack] = useState("");
  const [filterQuarter, setFilterQuarter] = useState("");
  const [filterPublished, setFilterPublished] = useState("");

  const { data, isLoading } = useAdminGetShowcasesQuery({
    ...(filterTrack ? { track: filterTrack } : {}),
    ...(filterQuarter ? { quarter: filterQuarter } : {}),
    ...(filterPublished ? { isPublished: filterPublished === "true" } : {}),
  });
  const showcases = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Showcases"
        subtitle="Schedule, publish, and manage student showcase events."
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Select value={filterTrack} onChange={setFilterTrack}>
          <option value="">All tracks</option>
          <option value="foundation_builders">Foundation</option>
          <option value="explorers">Explorers</option>
          <option value="innovators">Innovators</option>
          <option value="all">All tracks</option>
        </Select>
        <Select value={filterQuarter} onChange={setFilterQuarter}>
          <option value="">All quarters</option>
          <option value="Q1">Q1</option>
          <option value="Q2">Q2</option>
          <option value="Q3">Q3</option>
        </Select>
        <Select value={filterPublished} onChange={setFilterPublished}>
          <option value="">All statuses</option>
          <option value="true">Published</option>
          <option value="false">Drafts</option>
        </Select>
        <Button
          variant="primary"
          size="sm"
          className="ml-auto"
          onClick={() => {
            setEditing(null);
            setShowForm(!showForm);
          }}
        >
          {showForm ? "Cancel" : "+ New showcase"}
        </Button>
      </div>

      {showForm && !editing && (
        <div className="mb-5">
          <ShowcaseForm onDone={() => setShowForm(false)} />
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-52 rounded-[22px]" />
          ))}
        </div>
      ) : showcases.length === 0 ? (
        <EmptyState
          icon={Mic}
          title="No showcases yet"
          subtitle="Create a showcase event to schedule student presentations."
          action={
            <Button variant="primary" onClick={() => setShowForm(true)}>
              Create first showcase
            </Button>
          }
        />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {showcases.map((s: AdminShowcase) => (
            <div key={s.id}>
              {editing?.id === s.id ? (
                <ShowcaseForm existing={s} onDone={() => setEditing(null)} />
              ) : (
                <ShowcaseCard
                  showcase={s}
                  onEdit={() => setEditing(s)}
                  onParticipants={() => setParticipants(s)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {participants && (
        <ParticipantsPanel
          showcase={participants}
          onClose={() => setParticipants(null)}
        />
      )}
    </>
  );
}
